import { ThreeEvent } from '@react-three/fiber';

import { TrussEnvelope } from '@paragon/contract-library';
import { getModifierKeys } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../../components/hooks';
import {
  handleStandardSceneItemEditPropertiesAction,
  handleStandardSceneItemRightClick,
} from '../../../store/actions/sceneInteractionActions';
import { selectItem, selectItemByGuid } from '../../../store/actions/selectActions';
import { updatePickingObjectInfoForChosenTrussEnvelopes } from '../../../store/actions/trussGroups';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../../store/actions/viewportActions';
import { getPickingObjectInfo } from '../../../store/selectors/pickingSelectors';
import { threeJsDom } from '../../util/events/ThreeJSDom';

export function useTrussEnvelopeEventHandlers(envelope: TrussEnvelope, options?: { inert?: boolean }) {
  const dispatch = useAppDispatch();

  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);

  function onPointerMove(event: ThreeEvent<MouseEvent>) {
    event.stopPropagation();
  }

  if (options?.inert) return { onPointerMove };

  const interactionHandle = { type: 'trussEnvelope', guid: envelope.guid, origin: 'fiber' } as const;

  function onClick(event: ThreeEvent<MouseEvent>) {
    if (pickingObjectInfo?.type === 'pickingTrussEnvelopesForTrussGroup') {
      event.stopPropagation();
      dispatch(updatePickingObjectInfoForChosenTrussEnvelopes([envelope.guid]));
      return;
    } else if (event.detail === 2) {
      event.stopPropagation();

      if (envelope.componentDesignGuid) {
        dispatch(selectItemByGuid(envelope.componentDesignGuid, 'componentDesign'));
      } else {
        dispatch(handleStandardSceneItemEditPropertiesAction('trussEnvelope', envelope));
      }
    } else if (threeJsDom.shouldDispatchClickEvent()) {
      event.stopPropagation();
      dispatch(
        selectItem(envelope, 'trussEnvelope', {
          intent: 'toggle',
          exclusive: !getModifierKeys(event).ctrlKey,
        }),
      );
    }
  }

  async function onContextMenu(event: ThreeEvent<MouseEvent>) {
    await dispatch(handleStandardSceneItemRightClick(event, interactionHandle));
  }

  function onPointerOver(event: ThreeEvent<MouseEvent>) {
    event.stopPropagation();
    dispatch(ensureHoverItemIsActive(interactionHandle));
  }

  function onPointerOut() {
    dispatch(clearHoverItemIfActive(interactionHandle));
  }

  return { onClick, onContextMenu, onPointerOver, onPointerOut, onPointerMove };
}
